import { TextHighlighter } from '@components/textHighlighter';
import { Button } from '@components/ui/button';
import { Card, CardContent, CardHeader } from '@components/ui/card';
import { Form, FormField, FormMessage } from '@components/ui/form';
import { Input } from '@components/ui/form/input';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { PasswordInput } from '@components/ui/form/password';
import { useQueryParams } from '@hooks/queryParams';
import { appRoutes } from '@utils/consts';
import { parseRoute } from '@utils/i18n';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { usePanel } from '@hooks/usePanel';
import { FormRequired } from '@components/ui/form/required';
import { useEffect } from 'react';
import { useToast } from '@hooks/useToast';
import { useLoginForm } from '@forms/login';
import { LoginLayoutProps } from './types';

export const LoginLayout = ({ onSuccess, keepLocation }: LoginLayoutProps = {}) => {
  const {
    params: { redirect, expired },
    setParam,
  } = useQueryParams();

  const { panel, parseUrl } = usePanel();
  const { t } = useTranslation('app');
  const navigate = useNavigate();
  const { toast } = useToast();

  const { form, onSubmit, locked } = useLoginForm({
    keepLocation,
    successHandler: () => {
      if (onSuccess) return onSuccess();
      void navigate(`/${parseRoute(redirect ? redirect : panel ? appRoutes.tenants : '')}`);
    },
  });

  useEffect(() => {
    if (!expired) return;
    setParam('expired', '');
    toast({ title: t('pages.login.sessionExpired') });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expired]);

  return (
    <Card className="mx-auto max-w-md rounded-lg border-0 font-inter shadow-card">
      <CardHeader className="flex-row">
        <h1 className="text-heading-xs font-bold">{t('pages.login.title')}</h1>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-10">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field, fieldState: { error } }) => (
                  <FormItemWrapper required label={t('forms.email.label')}>
                    <Input error={error?.message} {...field} />
                  </FormItemWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field, fieldState: { error } }) => (
                  <FormItemWrapper required label={t('forms.password.label')}>
                    <PasswordInput error={error?.message} {...field} />
                  </FormItemWrapper>
                )}
              />
              <FormRequired />
              <FormField name="root" render={() => <FormMessage />} />
            </div>
            <div className="flex justify-end gap-1">
              <Button type="submit" disabled={locked}>
                {t('pages.login.action')}
              </Button>
            </div>
            <div>
              <Link className="link" to={`/${parseRoute(parseUrl(appRoutes.passwordRecovery))}`}>
                <TextHighlighter text={t('pages.login.link')} wordsIndexes={[1]} />
              </Link>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
